/*
 * Reviews hub: aggregates review data from multiple sources (Shopify, eBay),
 * renders a headline rating and a distribution, and provides client-side
 * search, sentiment filtering and sorting.
 *
 * Reviews are never separated by source. There is no source filter and no
 * per-source scoreboard - the whole set is shown together, and provenance is
 * stated once in the summary. Source only reappears as a per-card badge when
 * more than one source actually has reviews in it, so the visitor can still
 * tell them apart when it means something.
 *
 * Data comes from snippets/reviews-data.liquid, inlined by sections/reviews-hub.liquid
 * as <script type="application/json">.
 *
 * RATING MODEL
 * Sources do not agree on how they rate. Shopify-style sources publish a real
 * 1-5 star rating; eBay publishes only positive / neutral / negative and has
 * no star scale at all. Rather than inventing stars for eBay, each review
 * carries a `sentiment` and, only where the source genuinely provides one, a
 * `rating`. The hub picks its headline accordingly:
 *
 *   every review has stars -> "4.8 out of 5"
 *   no review has stars    -> "100% positive"   (what eBay actually states)
 *   a mix                  -> positive % headline, star average shown beside it
 *
 * This matters beyond pedantry: publishing a star average nobody awarded is a
 * misleading-review problem, not just an accuracy one.
 */
const SENTIMENT_KEYS = ['positive', 'neutral', 'negative'];
const SENTIMENT_RANK = { positive: 3, neutral: 2, negative: 1 };
const PERIOD_RANK = {
  'Past month': 0,
  'Past 6 months': 1,
  'Past year': 2,
  'More than a year ago': 3,
};

class ReviewsHub extends HTMLElement {
  constructor() {
    super();

    const data = this.parseData();
    this.reviews = data.reviews;
    this.delivery = data.delivery;
    this.state = { query: '', sort: 'newest' };

    this.searchInput = this.querySelector('[data-reviews-search]');
    this.clearButton = this.querySelector('[data-reviews-clear]');
    this.sortSelect = this.querySelector('[data-reviews-sort]');
    this.listEl = this.querySelector('[data-reviews-list]');
    this.statusEl = this.querySelector('[data-reviews-status]');
    this.emptyEl = this.querySelector('[data-reviews-empty]');
    this.summaryEl = this.querySelector('[data-reviews-summary]');
    this.warningEl = this.querySelector('[data-reviews-warning]');
    this.deliveryEl = this.querySelector('[data-reviews-delivery]');

    this.labels = {
      showing: this.dataset.labelShowing || 'Showing [count] of [total] reviews',
      verified: this.dataset.labelVerified || 'Verified',
      shopify: this.dataset.labelShopify || 'Store',
      ebay: this.dataset.labelEbay || 'eBay',
      positive: this.dataset.labelPositive || 'Positive',
      neutral: this.dataset.labelNeutral || 'Neutral',
      negative: this.dataset.labelNegative || 'Negative',
    };
  }

  connectedCallback() {
    if (!this.reviews.length) {
      this.renderEmptyDataset();
      return;
    }

    // Which sources actually have reviews. Drives the provenance line and
    // whether per-card source badges are worth showing at all.
    const { sources } = this.stats;
    this.sourcesPresent = Object.keys(sources).filter((key) => sources[key] > 0);

    this.renderWarning();
    this.renderSummary();
    this.renderDelivery();
    this.relabelSort();
    this.bindEvents();
    this.update();
  }

  parseData() {
    const empty = { reviews: [], delivery: null };
    const node = this.querySelector('[data-reviews-json]');
    if (!node) return empty;

    let parsed;
    try {
      parsed = JSON.parse(node.textContent);
    } catch (error) {
      console.error('Reviews hub: could not parse reviews-data.liquid as JSON.', error);
      return empty;
    }

    const list = Array.isArray(parsed) ? parsed : parsed.reviews;
    if (!Array.isArray(list)) return empty;

    const reviews = list
      .map((review, index) => {
        // A rating only counts if the source actually published one.
        const raw = parseInt(review.rating, 10);
        const rating = Number.isFinite(raw) ? Math.min(5, Math.max(1, raw)) : null;

        return {
          id: String(review.id || `review-${index}`),
          source: review.source === 'ebay' ? 'ebay' : 'shopify',
          author: String(review.author || 'Anonymous'),
          rating,
          sentiment: SENTIMENT_KEYS.includes(review.sentiment)
            ? review.sentiment
            : this.ratingToSentiment(rating),
          title: String(review.title || ''),
          body: String(review.body || ''),
          date: String(review.date || ''),
          period: String(review.period || ''),
          price: String(review.price || ''),
          product: String(review.product || ''),
          verified: review.verified === true,
          placeholder: review.placeholder === true,
        };
      })
      .filter((review) => review.body);

    return { reviews, delivery: parsed.delivery || null };
  }

  ratingToSentiment(rating) {
    if (!rating) return 'positive';
    if (rating >= 4) return 'positive';
    return rating === 3 ? 'neutral' : 'negative';
  }

  /* ---------- stats ---------- */

  get stats() {
    const total = this.reviews.length;
    const sentiments = { positive: 0, neutral: 0, negative: 0 };
    const stars = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const sources = { shopify: 0, ebay: 0 };
    const rated = [];

    this.reviews.forEach((review) => {
      sentiments[review.sentiment] += 1;
      sources[review.source] += 1;
      if (review.rating) {
        stars[review.rating] += 1;
        rated.push(review.rating);
      }
    });

    // 'stars' only when every single review carries a real rating.
    let mode = 'sentiment';
    if (rated.length === total && total > 0) mode = 'stars';
    else if (rated.length > 0) mode = 'mixed';

    return {
      total,
      mode,
      sentiments,
      stars,
      sources,
      ratedCount: rated.length,
      starAverage: rated.length ? rated.reduce((a, b) => a + b, 0) / rated.length : null,
      /*
       * Floored, never rounded. 441 positive out of 442 is 99.77%, which
       * Math.round turns into "100% positive" - a claim of a spotless record
       * while a neutral review is sitting in the set and visible in the bars
       * directly below it. Flooring means 100% appears only when the set is
       * genuinely 100% positive, and the figure can never overstate.
       */
      positivePercent: total ? Math.floor((sentiments.positive / total) * 100) : 0,
      placeholders: this.reviews.filter((review) => review.placeholder).length,
    };
  }

  /* ---------- rendering ---------- */

  renderWarning() {
    if (!this.warningEl) return;
    const count = this.stats.placeholders;

    if (count === 0) {
      this.warningEl.hidden = true;
      return;
    }

    this.warningEl.hidden = false;
    this.warningEl.innerHTML = `<strong>${count} placeholder ${
      count === 1 ? 'entry' : 'entries'
    } present.</strong> These are sample rows in <code>snippets/reviews-data.liquid</code>, not real customer reviews. Replace or delete them before publishing this theme.`;
  }

  renderSummary() {
    if (!this.summaryEl) return;
    const stats = this.stats;
    const { total, mode } = stats;

    let headline;
    if (mode === 'stars') {
      const rounded = Math.round(stats.starAverage * 10) / 10;
      headline = `
        <span class="reviews-hub__average-number">${rounded.toFixed(1)}</span>
        <span class="reviews-hub__average-scale">out of 5</span>
        ${this.starsMarkup(Math.round(stats.starAverage))}`;
    } else {
      // eBay reports a positive percentage, not a score. Say exactly that.
      headline = `
        <span class="reviews-hub__average-number">${stats.positivePercent}%</span>
        <span class="reviews-hub__average-scale">${this.labels.positive.toLowerCase()} feedback</span>`;
    }

    // In mixed mode the star average is real but only covers part of the set,
    // so it is qualified rather than presented as the headline.
    const starNote =
      mode === 'mixed' && stats.starAverage
        ? `<span class="reviews-hub__average-note">${
            Math.round(stats.starAverage * 10) / 10
          } out of 5 across the ${stats.ratedCount} star-rated review${
            stats.ratedCount === 1 ? '' : 's'
          }</span>`
        : '';

    // No distribution bars. The headline percentage is the whole readout, so
    // the summary is a single centred block rather than a two-column split.
    this.summaryEl.innerHTML = `
      <div class="reviews-hub__average">
        ${headline}
        <span class="reviews-hub__average-count">Based on ${total} review${total === 1 ? '' : 's'}</span>
        ${starNote}
        <div class="reviews-hub__sources">${this.provenanceMarkup(stats)}</div>
        ${this.sourceLinkMarkup()}
      </div>
    `;

    this.injectStructuredData(stats);
  }

  /*
   * Where the reviews came from, stated once. Not a filter and not a
   * scoreboard - but it still has to be said, because reviews collected on
   * eBay are not reviews left on this store and presenting them as if they
   * were would be misleading.
   */
  provenanceMarkup(stats) {
    const present = this.sourcesPresent || [];
    if (present.length !== 1) {
      return present
        .map((key) => `<span>${this.labels[key]}: ${stats.sources[key]}</span>`)
        .join('');
    }

    const where = present[0] === 'ebay' ? `our ${this.labels.ebay} shop` : 'this store';
    // "A selection of", not "every". Reviews whose wording could read as
    // critical are filtered out by EXCLUDE_PATTERNS in build-reviews-data.py,
    // so this set is not complete and must not be described as if it were.
    // The source link below carries the visitor to the unfiltered record.
    return `<span>A selection of the feedback buyers have left on ${where}</span>`;
  }

  /*
   * Link straight to the eBay feedback profile. The reviews here are a copy of
   * a public record, so pointing at the original is the thing that makes them
   * checkable - a visitor can confirm the lot without taking our word for it.
   */
  sourceLinkMarkup() {
    const url = this.dataset.sourceUrl;
    if (!url) return '';

    const label = this.dataset.sourceLinkLabel || 'View our feedback on eBay';
    return `
      <a
        class="reviews-hub__source-link"
        href="${this.escape(url)}"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>${this.escape(label)}</span>
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" width="12" height="12">
          <path
            d="M6 2h8v8h-2V5.4L4.7 12.7 3.3 11.3 10.6 4H6z"
            fill="currentColor"
          />
        </svg>
        <span class="visually-hidden">(opens in a new tab)</span>
      </a>`;
  }

  /*
   * eBay's automated "delivered on time" entries. These are generated by eBay
   * from tracking when a buyer leaves no comment - nobody wrote them - so they
   * are shown as a delivery record and deliberately excluded from the review
   * count, the positive percentage and any structured data.
   */
  renderDelivery() {
    if (!this.deliveryEl) return;
    if (this.dataset.showDelivery !== 'true' || !this.delivery || !this.delivery.count) {
      this.deliveryEl.hidden = true;
      return;
    }

    const { count, periods = [], products = [] } = this.delivery;

    const periodList = periods.length
      ? `<ul class="reviews-hub__delivery-breakdown">${periods
          .map(
            (p) =>
              `<li><strong>${p.count}</strong> <span>${this.escape(p.label)}</span></li>`
          )
          .join('')}</ul>`
      : '';

    const productList = products.length
      ? `<ul class="reviews-hub__delivery-products">${products
          .map(
            (p) =>
              `<li><span>${this.escape(p.name)}</span><strong>${p.count}</strong></li>`
          )
          .join('')}</ul>`
      : '';

    this.deliveryEl.hidden = false;
    this.deliveryEl.innerHTML = `
      <div class="reviews-hub__delivery-head">
        <span class="reviews-hub__delivery-number">${count.toLocaleString()}</span>
        <span class="reviews-hub__delivery-label">further orders confirmed delivered on time</span>
      </div>
      <p class="reviews-hub__delivery-note">
        eBay records these automatically from tracking when an order arrives on time
        and the buyer raises no issue. They are <strong>not customer reviews</strong>
        and are not counted in the ${this.reviews.length} above.
      </p>
      ${periodList}
      ${productList}
    `;
  }

  starsMarkup(rating, small = false) {
    const stars = [1, 2, 3, 4, 5]
      .map((star) => {
        const on = star <= rating;
        return `<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path class="reviews-hub__star--${
          on ? 'on' : 'off'
        }" d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9z"/></svg>`;
      })
      .join('');

    return `<span class="reviews-hub__stars${
      small ? ' reviews-hub__stars--sm' : ''
    }" role="img" aria-label="${rating} out of 5 stars">${stars}</span>`;
  }

  /* A star row where the source gave stars, a sentiment pill where it did not. */
  ratingMarkup(review) {
    if (review.rating) return this.starsMarkup(review.rating, true);

    return `<span class="reviews-hub__sentiment reviews-hub__sentiment--${review.sentiment}">
      ${this.labels[review.sentiment]}
    </span>`;
  }

  renderList(reviews) {
    // A source badge on every card is noise when every card says the same
    // thing, so it only appears once there is more than one source to tell
    // apart. The summary carries the provenance in the single-source case.
    const showBadge = (this.sourcesPresent || []).length > 1;

    this.listEl.innerHTML = reviews
      .map((review) => {
        const badge = review.source === 'ebay' ? this.labels.ebay : this.labels.shopify;
        // eBay exposes only a relative bucket ("Past 6 months"), never a date.
        const when = review.date ? this.formatDate(review.date) : this.escape(review.period);

        const meta = [
          this.escape(review.author),
          when,
          review.product ? this.highlight(review.product) : '',
          review.price ? this.escape(review.price) : '',
          review.verified ? this.labels.verified : '',
        ].filter(Boolean);

        return `
          <li class="reviews-hub__card">
            <div class="reviews-hub__card-head">
              ${this.ratingMarkup(review)}
              ${showBadge ? `<span class="reviews-hub__badge">${badge}</span>` : ''}
            </div>
            ${review.title ? `<h3 class="reviews-hub__card-title">${this.highlight(review.title)}</h3>` : ''}
            <p class="reviews-hub__card-body">${this.highlight(review.body)}</p>
            <div class="reviews-hub__card-meta">${meta.map((item) => `<span>${item}</span>`).join('')}</div>
          </li>`;
      })
      .join('');
  }

  renderEmptyDataset() {
    if (this.statusEl) {
      this.statusEl.textContent =
        'No reviews found. Add entries to snippets/reviews-data.liquid.';
    }
  }

  /* "Highest rated" is meaningless with no ratings - say what it does. */
  relabelSort() {
    if (!this.sortSelect || this.stats.mode === 'stars') return;

    const rename = { highest: this.labels.positive + ' first', lowest: 'Most critical first' };
    Array.from(this.sortSelect.options).forEach((option) => {
      if (rename[option.value]) option.textContent = rename[option.value];
    });
  }

  /* ---------- filtering ---------- */

  recencyRank(review) {
    return review.date ? -1 : PERIOD_RANK[review.period] ?? 9;
  }

  get filtered() {
    const query = this.state.query.trim().toLowerCase();

    // Search is the only filter. Nothing is withheld by rating or sentiment.
    const result = this.reviews.filter((review) => {
      if (!query) return true;

      return [review.author, review.title, review.body, review.product]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });

    const score = (review) => review.rating || SENTIMENT_RANK[review.sentiment];
    const sorters = {
      newest: (a, b) =>
        this.recencyRank(a) - this.recencyRank(b) || (b.date || '').localeCompare(a.date || ''),
      oldest: (a, b) =>
        this.recencyRank(b) - this.recencyRank(a) || (a.date || '').localeCompare(b.date || ''),
      highest: (a, b) => score(b) - score(a),
      lowest: (a, b) => score(a) - score(b),
    };

    return result.sort(sorters[this.state.sort] || sorters.newest);
  }

  update() {
    const reviews = this.filtered;
    const total = this.reviews.length;

    this.renderList(reviews);

    if (this.statusEl) {
      this.statusEl.textContent = this.labels.showing
        .replace('[count]', reviews.length)
        .replace('[total]', total);
    }

    if (this.emptyEl) this.emptyEl.hidden = reviews.length > 0;
    if (this.clearButton) this.clearButton.hidden = this.state.query === '';
  }

  bindEvents() {
    if (this.searchInput) {
      let timer;
      this.searchInput.addEventListener('input', (event) => {
        clearTimeout(timer);
        const value = event.target.value;
        timer = setTimeout(() => {
          this.state.query = value;
          this.update();
        }, 200);
      });
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.state.query = '';
        if (this.searchInput) {
          this.searchInput.value = '';
          this.searchInput.focus();
        }
        this.update();
      });
    }

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (event) => {
        this.state.sort = event.target.value;
        this.update();
      });
    }
  }

  /* ---------- helpers ---------- */

  escape(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  highlight(value) {
    const safe = this.escape(value);
    const query = this.state.query.trim();
    if (!query) return safe;

    const pattern = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safe.replace(new RegExp(`(${pattern})`, 'gi'), '<mark>$1</mark>');
  }

  formatDate(value) {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return this.escape(value);
    return date.toLocaleDateString(document.documentElement.lang || 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /*
   * Only ever emit a rating Google can trust. An AggregateRating built from
   * sentiment we mapped to stars ourselves would be a rating no customer gave,
   * so sentiment-only sets are skipped entirely and mixed sets report only the
   * genuinely star-rated subset.
   */
  injectStructuredData(stats) {
    if (this.dataset.structuredData !== 'true') return;
    if (!stats.ratedCount || stats.starAverage === null) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: this.dataset.shopName || '',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Math.round(stats.starAverage * 10) / 10,
        reviewCount: stats.ratedCount,
        bestRating: 5,
        worstRating: 1,
      },
    });
    document.head.appendChild(script);
  }
}

if (!customElements.get('reviews-hub')) {
  customElements.define('reviews-hub', ReviewsHub);
}
